import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import PortfolioHoldings from "../components/PortfolioHoldings";
import {
    Briefcase,
    TrendingUp,
    TrendingDown,
    RefreshCw,
    Plus,
    ArrowUpRight,
    Bell,
    Settings,
    Mail,
    CheckCircle2,
    Trash2,
    Loader2,
    Upload
} from "lucide-react";

export default function PortfolioPage() {
    const [portfolios, setPortfolios] = useState([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [syncEmails, setSyncEmails] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [verifyingEmail, setVerifyingEmail] = useState(null);
    const [verificationToken, setVerificationToken] = useState("");
    const [uploading, setUploading] = useState(false);
    const [stats, setStats] = useState({
        totalValue: 0,
        dayGain: 0,
        roi: 0
    });

    useEffect(() => {
        fetchPortfolios();
    }, []);

    const fetchPortfolios = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/portfolios/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPortfolios(response.data);
            if (response.data.length > 0 && !selectedPortfolio) {
                setSelectedPortfolio(response.data[0]);
            }

            // Calculate aggregate stats
            const total = response.data.reduce((acc, p) => acc + parseFloat(p.total_value || 0), 0);
            const gain = response.data.reduce((acc, p) => acc + parseFloat(p.day_gain || 0), 0);
            setStats({
                totalValue: total,
                dayGain: gain,
                roi: total > 0 ? (gain / total) * 100 : 0
            });
        } catch (error) {
            console.error("Error fetching portfolios:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        try {
            setSyncing(true);
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/portfolios/sync/`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Give the AI parsing some time before refreshing the list
            setTimeout(fetchPortfolios, 2000);
        } catch (error) {
            console.error("Sync failed:", error);
            setSyncing(false);
        }
    };

    const fetchSyncEmails = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/portfolios/sync-emails/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSyncEmails(response.data);
        } catch (error) {
            console.error("Error fetching sync emails:", error);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.pdf')) {
            alert("Please upload a PDF file.");
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/portfolios/upload_statement/`, formData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                },
            });

            alert("Portfolio synced successfully from PDF!");
            fetchPortfolios();
            setShowSettingsModal(false);
        } catch (error) {
            console.error("Upload failed:", error);
            alert(error.response?.data?.error || "Failed to parse PDF. Please ensure it is a supported broker statement.");
        } finally {
            setUploading(false);
        }
    };

    const handleAddEmail = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/portfolios/sync-emails/`, { email: newEmail }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNewEmail("");
            fetchSyncEmails();
            alert("Email added. Please check your inbox for a verification code if it's not your primary email.");
        } catch (error) {
            console.error("Error adding email:", error);
            alert("Failed to add email. It might already be added.");
        }
    };

    const handleVerifyEmail = async (emailId) => {
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/portfolios/sync-emails/${emailId}/verify/`, { token: verificationToken }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVerifyingEmail(null);
            setVerificationToken("");
            fetchSyncEmails();
            alert("Email verified!");
        } catch (error) {
            console.error("Error verifying email:", error);
            alert("Invalid verification code.");
        }
    };

    const handleDeleteEmail = async (emailId) => {
        if (!window.confirm("Are you sure you want to remove this sync email?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_BASE_URL}/api/portfolios/sync-emails/${emailId}/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchSyncEmails();
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    useEffect(() => {
        if (showSettingsModal) {
            fetchSyncEmails();
        }
    }, [showSettingsModal]);

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Briefcase className="text-blue-600" />
                        My Portfolios
                    </h1>
                    <p className="text-gray-500 mt-1">Manage and track your synced assets across brokers.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleSync}
                        disabled={syncing}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm font-medium text-gray-600"
                    >
                        <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                        {syncing ? "Sync Now" : "Sync Now"}
                    </button>
                    <button
                        onClick={() => setShowSettingsModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
                    >
                        <Settings size={16} />
                        Portfolio Settings
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <p className="text-sm font-medium text-gray-500 mb-1">Total Net Worth</p>
                    <h3 className="text-3xl font-bold text-gray-900">${stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                    <div className="flex items-center gap-1 mt-2 text-green-600 font-medium text-sm">
                        <ArrowUpRight size={14} />
                        <span>All-time high synced</span>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <p className="text-sm font-medium text-gray-500 mb-1">Day's Gain/Loss</p>
                    <div className="flex items-baseline gap-2">
                        <h3 className={`text-3xl font-bold ${stats.dayGain >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {stats.dayGain >= 0 ? "+" : ""}${Math.abs(stats.dayGain).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </h3>
                        <span className={`text-sm font-medium ${stats.dayGain >= 0 ? "text-green-500" : "text-red-500"}`}>
                            ({stats.roi.toFixed(2)}%)
                        </span>
                    </div>
                    <div className="mt-2">
                        {stats.dayGain >= 0 ?
                            <TrendingUp className="text-green-500" size={20} /> :
                            <TrendingDown className="text-red-500" size={20} />
                        }
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <p className="text-sm font-medium text-gray-500 mb-1">Active Portfolios</p>
                    <h3 className="text-3xl font-bold text-gray-900">{portfolios.length}</h3>
                    <div className="mt-2 flex items-center gap-2">
                        {portfolios.map((p, i) => (
                            <div key={p.id} className="h-2 w-8 bg-blue-600 rounded-full" title={p.name} style={{ opacity: 1 - (i * 0.2) }}></div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden min-h-[400px]">
                {/* Tabs / Selectors */}
                {portfolios.length > 0 && (
                    <div className="flex border-b border-gray-50 bg-gray-50/50">
                        {portfolios.map(p => (
                            <button
                                key={p.id}
                                onClick={() => setSelectedPortfolio(p)}
                                className={`px-8 py-4 text-sm font-medium transition-all relative ${selectedPortfolio?.id === p.id
                                    ? "text-blue-600 bg-white shadow-sm"
                                    : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
                                    }`}
                            >
                                {p.name}
                                {selectedPortfolio?.id === p.id && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* Holdings Table */}
                <div className="p-0">
                    {loading ? (
                        <div className="p-20 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-500 text-sm">Loading portfolios...</p>
                        </div>
                    ) : selectedPortfolio ? (
                        <PortfolioHoldings portfolioId={selectedPortfolio.id} />
                    ) : (
                        <div className="p-20 text-center">
                            <Briefcase className="mx-auto text-gray-200 mb-4" size={64} />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No portfolios linked yet</h3>
                            <p className="text-gray-500 max-w-sm mx-auto mb-8">
                                Connect your broker statements by sending them to our secure sync email address.
                            </p>
                            <button
                                onClick={() => setShowSettingsModal(true)}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold shadow-md hover:shadow-lg"
                            >
                                <Settings size={18} />
                                Connect Your Portfolio
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Portfolio Settings Modal (Unified) */}
            {showSettingsModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">Portfolio Settings</h1>
                                <p className="text-xs text-gray-500 mt-0.5">Manage your broker connections and authorized emails.</p>
                            </div>
                            <button
                                onClick={() => setShowSettingsModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                            >
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>
                        <div className="p-0 max-h-[75vh] overflow-y-auto">
                            {/* Step 1: Instructions */}
                            <div className="p-6 border-b border-gray-50 bg-blue-50/30">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-lg">
                                        <Bell size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-blue-900 text-lg">How to Sync</h3>
                                        <p className="text-sm text-blue-700 leading-relaxed mt-1">
                                            Forward your latest broker statement (PDF) to our secure sync email address. Our AI will automatically extract and update your holdings.
                                        </p>
                                        <div className="mt-4 flex flex-col sm:flex-row gap-3">
                                            <div className="flex-1 bg-white border border-blue-100 rounded-xl px-4 py-3 flex items-center justify-between">
                                                <code className="text-sm font-bold text-blue-800">sync@traderbro.ai</code>
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText("sync@traderbro.ai");
                                                        alert("Email copied!");
                                                    }}
                                                    className="text-[10px] font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wider"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                            <button
                                                onClick={handleSync}
                                                disabled={syncing}
                                                className="px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                                            >
                                                <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                                                {syncing ? "Processing..." : "I've Sent the Email"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 space-y-8">
                                {/* Manual Upload Section */}
                                <div className="space-y-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                                            <Upload size={18} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900">Manual PDF Upload</h3>
                                            <p className="text-[11px] text-gray-400">Upload your statement directly if you don't want to forward email.</p>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleFileUpload}
                                            disabled={uploading}
                                            className="hidden"
                                            id="portfolio-upload"
                                        />
                                        <label
                                            htmlFor="portfolio-upload"
                                            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${uploading ? 'bg-gray-100 border-gray-300 cursor-wait' : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'}`}
                                        >
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <Upload className={`mb-3 ${uploading ? 'animate-bounce text-blue-400' : 'text-gray-400'}`} size={24} />
                                                <p className="mb-1 text-sm text-gray-500 font-semibold">
                                                    {uploading ? "Analyzing Statement..." : "Click to upload portfolio PDF"}
                                                </p>
                                                <p className="text-xs text-gray-400">PDF (MAX. 10MB)</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Email Management Section */}
                                <div className="space-y-4">
                                    <div className="flex justify-between items-end px-1">
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900">Authorized Senders</h3>
                                            <p className="text-[11px] text-gray-400 mt-0.5">We only process emails sent from these addresses.</p>
                                        </div>
                                    </div>

                                    <form onSubmit={handleAddEmail} className="flex gap-2">
                                        <input
                                            type="email"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            placeholder="Add forwarding email..."
                                            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                            required
                                        />
                                        <button
                                            type="submit"
                                            className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold hover:bg-gray-50 transition-all"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </form>

                                    <div className="grid gap-2">
                                        {syncEmails.map(email => (
                                            <div key={email.id} className="flex items-center justify-between p-3 bg-gray-50/50 border border-gray-100 rounded-xl group hover:border-blue-100 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${email.is_verified ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                                                        {email.is_verified ? <CheckCircle2 size={16} /> : <Mail size={16} />}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-gray-900 truncate max-w-[180px]">
                                                            {email.email}
                                                        </div>
                                                        <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                                                            {email.is_verified ? "Verified" : "Pending Code"}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!email.is_verified && (
                                                        <button
                                                            onClick={() => setVerifyingEmail(email.id)}
                                                            className="px-2.5 py-1 text-[10px] font-bold bg-amber-600 text-white rounded-md hover:bg-amber-700 shadow-sm"
                                                        >
                                                            Verify
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteEmail(email.id)}
                                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Supported Brokers Section */}
                                <div className="space-y-3 pt-4 border-t border-gray-50">
                                    <h3 className="text-sm font-bold text-gray-900 px-1">Supported Brokers</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {['Interactive Brokers', 'Merrill Lynch', 'Charles Schwab', 'Fidelity', 'Sheltech', 'EBL Securities'].map(b => (
                                            <span key={b} className="px-3 py-1 bg-gray-50 border border-gray-100 rounded-full text-[11px] font-medium text-gray-600">
                                                {b}
                                            </span>
                                        ))}
                                        <span className="px-3 py-1 bg-blue-50 border border-blue-100 rounded-full text-[11px] font-bold text-blue-600">
                                            + Dynamic (AI-Powered)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Verification Overlay */}
                        {verifyingEmail && (
                            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-[60] flex items-center justify-center p-8 animate-in fade-in duration-200">
                                <div className="max-w-sm w-full space-y-6 text-center">
                                    <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto text-white shadow-xl rotate-3">
                                        <CheckCircle2 size={32} />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">Verify Email</h3>
                                        <p className="text-gray-500 mt-2 text-sm">Enter the code sent to your email.</p>
                                    </div>
                                    <div className="space-y-4">
                                        <input
                                            value={verificationToken}
                                            onChange={(e) => setVerificationToken(e.target.value.toUpperCase())}
                                            placeholder="EX: ABC123D"
                                            className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-6 py-4 text-center font-mono text-xl tracking-[0.5em] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all shadow-inner"
                                        />
                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => {
                                                    setVerifyingEmail(null);
                                                    setVerificationToken("");
                                                }}
                                                className="flex-1 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() => handleVerifyEmail(verifyingEmail)}
                                                className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-lg hover:bg-blue-700 transition-all"
                                            >
                                                Confirm
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="p-4 bg-gray-50/50 border-t border-gray-100">
                            <button
                                onClick={() => setShowSettingsModal(false)}
                                className="w-full py-3 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
